import moment from 'moment';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Persona from '@r6digital/rapidstor-types/types/Persona';
import * as Sentry from '@sentry/react';

export const logEvent = (severity: number, message: string) => {
    if (import.meta.env.MODE === 'development') {
        switch (severity) {
            case 3:
                console.error(`[${severity}] ${message}`);
                break;
            case 2:
                console.warn(`[${severity}] ${message}`);
                break;
            case 1:
                console.info(`[${severity}] ${message}`);
                break;
            default:
                console.info(`[${severity}] ${message}`);
                break;
        }
    } else {
        if (severity === 3 || severity === 2) {
            Sentry.captureMessage(message);
        }
    }
};

export const findPersonaId = (
    typeValue: string | null,
    personas: Persona[] | undefined
) => {
    if (!typeValue) return null;
    if (!personas) return null;
    for (const p of personas) {
        for (const params of p.parameters) {
            if (params.name === 'personatype' && params.value === typeValue) {
                return p._id;
            }
        }
    }
    return null;
};

export const pickUnitType = (descriptor: Descriptor): UnitType => {
    const firstAvailable = descriptor.unitTypes.find(
        (unitType) => unitType.totalAvailable > 0
    );
    if (firstAvailable) {
        return firstAvailable;
    }
    const firstInGroup = descriptor.unitTypes[0];
    return firstInGroup;
};

export const isDescriptorAvailable = (descriptor: Descriptor): boolean => {
    return descriptor.unitTypes.some((unitType) => unitType.totalAvailable > 0);
};

export const getEarliestMoveInDate = (location: Location): string => {
    let minDate = moment.utc();

    if (location.rapidstor.siteOpeningDate) {
        minDate = moment.utc(location.rapidstor.siteOpeningDate, 'YYYY-MM-DD');
    }

    if (location.rapidstor.reservationNumberOfDaysInAdvanceMinimum) {
        const advanceDays = location.rapidstor.reservationNumberOfDaysInAdvanceMinimum;
        const advanceDate = moment.utc().add(advanceDays, 'days');
        if (minDate.isBefore(advanceDate)) {
            minDate = advanceDate;
        }
    }

    if (location.rapidstor.datesClosed) {
        const datesClosed = location.rapidstor.datesClosed
            .split(',')
            .map((date) => moment.utc(date.trim(), 'YYYY-MM-DD'))
            .sort((a, b) => a.valueOf() - b.valueOf());

        for (const closedDate of datesClosed) {
            if (closedDate.isSame(minDate, 'day')) {
                minDate = minDate.add(1, 'day');
                // Ensure minDate is not before siteOpeningDate
                if (location.rapidstor.siteOpeningDate) {
                    const siteOpeningMoment = moment.utc(
                        location.rapidstor.siteOpeningDate,
                        'YYYY-MM-DD'
                    );
                    if (minDate.isBefore(siteOpeningMoment)) {
                        minDate = siteOpeningMoment;
                    }
                }
            }
        }
    }

    return minDate.toISOString();
};
